import { type FC, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { type Store } from 'redux';
import { Provider } from 'react-redux';
import { type Environment, RelayEnvironmentProvider } from 'react-relay';

import { IntlProvider } from 'dibs-react-intl';
import serverVars from 'server-vars';

import { HeaderFooterServerVarsContextProvider } from '../components/HeaderFooterServerVarsContext/HeaderFooterServerVarsContext.tsx';
import { actionCreators } from '../actions/index.ts';

const getBuyerFooterClient = async ({
    store,
    relayEnv,
}: {
    store: Store;
    relayEnv: Environment;
}): Promise<{
    updateUserState: () => void;
}> => {
    const locale = serverVars.get('locale');
    const messages = serverVars.get('messages');
    const isMobile: boolean = serverVars.get('settings.isMobile');
    const buyerHost: string = serverVars.get('buyerHost');
    const isAppDownloadCopyAbTestVariant: boolean = serverVars.get(
        'isAppDownloadCopyAbTestVariant'
    );

    const Wrapper: FC<{ children?: ReactNode }> = ({ children }) => {
        return (
            <IntlProvider locale={locale} defaultLocale="en" messages={messages}>
                <RelayEnvironmentProvider environment={relayEnv}>
                    <HeaderFooterServerVarsContextProvider
                        isMobile={isMobile}
                        buyerHost={buyerHost}
                        isAppDownloadCopyAbTestVariant={isAppDownloadCopyAbTestVariant}
                    >
                        <Provider store={store}>{children}</Provider>
                    </HeaderFooterServerVarsContextProvider>
                </RelayEnvironmentProvider>
            </IntlProvider>
        );
    };

    if (isMobile) {
        const node = document.getElementById('js-footer');

        const messageBannerPromise = import(
            /* webpackChunkName: "MobileFooterMessageBanner" */ '../components/MessageBanner/MobileFooterMessageBanner.tsx'
        );

        const [{ render }, { default: MobileFooterMessageBanner }] = await Promise.all([
            import(/* webpackChunkName: "mobileFooter" */ '../components/mobileFooter.tsx'),
            messageBannerPromise,
        ]);

        if (node) {
            const div = document.createElement('div');
            div.className = 'footer-message-banner';
            node.parentNode?.insertBefore(div, node.nextSibling);
            const root = createRoot(div, { identifierPrefix: 'footer' });
            root.render(
                <Wrapper>
                    <MobileFooterMessageBanner environment={relayEnv} />
                </Wrapper>
            );
            await render({ node, Wrapper });
        }
    } else {
        const { render } = await import(
            /* webpackChunkName: "responsiveFooter" */ '../components/responsiveFooter.tsx'
        );
        const node = document.getElementById('js-footer');
        if (node) {
            await render({ node, Wrapper });
        }
    }

    return {
        updateUserState: store.dispatch.bind(null, actionCreators.setUpdateUserState(true)),
    };
};

export { getBuyerFooterClient };
